import { useMemo } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';

import { endpoints, fetcher } from '@/utils/axios';
import type { IOrder } from '@/interfaces/bookingContext';

/**
 * Shared SWR configuration used by all cart-related hooks in this module.
 *
 * Notes:
 * - `revalidateOnFocus: false` avoids refetching when the browser tab gains focus.
 * - `revalidateOnReconnect: true` refetches when the network comes back online.
 * - `refreshWhenOffline/Hidden: true` keeps SWR behavior consistent across app states.
 * - `refreshInterval: 0` disables polling (no periodic refetch).
 */
const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnMount: true,
  revalidateOnReconnect: true,
  refreshWhenOffline: true,
  refreshWhenHidden: true,
  refreshInterval: 0
};

/**
 * Fetches all cart items for a given user.
 *
 * @param userId - The user identifier. If undefined/empty, the request is skipped.
 *
 * @returns An object with:
 * - `cartItems`: cart items array (defaults to `[]` if not loaded yet)
 * - `cartLoading`: true while the request is loading
 * - `cartError`: AxiosError if the request failed
 * - `cartValidating`: true while SWR is revalidating
 * - `cartMutate`: SWR mutate function to update/refetch the cache
 *
 * @example
 * const { cartItems, cartLoading, cartError, cartMutate } = useGetCart(userId);
 * if (cartLoading) return <Spinner />;
 * if (cartError) return <ErrorView />;
 * return <CartList items={cartItems} onRefresh={() => cartMutate()} />;
 */
export const useGetCart = (userId?: string) => {
  /**
   * SWR key:
   * - When `key` is `null`, SWR will not run the fetcher (request is skipped).
   * - This prevents accidental calls like `/carts/undefined`.
   */
  const key = userId ? `${endpoints.cart}/carts/${userId}` : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IOrder[], AxiosError>(
    key,
    fetcher,
    swrConfig
  );

  return useMemo(
    () => ({
      cartItems: data ?? [],
      cartLoading: isLoading,
      cartError: error,
      cartValidating: isValidating,
      cartMutate: mutate
    }),
    [data, error, isLoading, isValidating, mutate]
  );
};

/**
 * Fetches a single cart item (order) by user and item identifiers.
 *
 * @param userId - The user identifier. If undefined/empty, the request is skipped.
 * @param itemId - The cart item identifier. If undefined/empty, the request is skipped.
 *
 * @returns An object with:
 * - `cart`: a single IOrder (defaults to `null` if not loaded yet)
 * - `cartLoading`: true while the request is loading
 * - `cartError`: AxiosError if the request failed
 * - `cartValidating`: true while SWR is revalidating
 * - `cartMutate`: SWR mutate function to update/refetch the cache
 *
 * @example
 * const { cart, cartLoading } = useGetCartOrder(userId, itemId);
 * if (cartLoading) return <Spinner />;
 * if (!cart) return null;
 * return <CartItemDetails item={cart} />;
 */
export const useGetCartOrder = (userId?: string, itemId?: string) => {
  /**
   * Skip request until both ids are available.
   * This is especially useful when ids come from router params or async auth state.
   */
  const key = userId && itemId ? `${endpoints.cart}/carts/${userId}/${itemId}` : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IOrder, AxiosError>(
    key,
    fetcher,
    swrConfig
  );

  return useMemo(
    () => ({
      cart: data ?? null,
      cartLoading: isLoading,
      cartError: error,
      cartValidating: isValidating,
      cartMutate: mutate
    }),
    [data, error, isLoading, isValidating, mutate]
  );
};

/**
 * Saves (updates) a cart item via HTTP PUT.
 *
 * Important:
 * - This is NOT a React hook (despite the previous name you had).
 * - Do NOT prefix it with `use` unless it actually uses React hooks.
 *
 * @param data - The cart item payload to be saved. Must include `cart_id` and `item_id`.
 * @returns Axios promise (you can await it). `response.data` depends on your API.
 *
 * @example
 * await saveCartItem(order);
 * // optionally: cartMutate() to refresh SWR cache
 */
export const saveCartItem = (data: IOrder) => {
  const url = `${endpoints.cart}/carts/${data.cart_id}/${data.item_id}`;
  return axios.put(url, data);
};
